import { useCallback } from 'react';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { useAppNavigation } from '@/context/AppNavigationContext.tsx';
import { useCreditBalance } from '@/hooks/payment/useCreditBalance.ts';
import { usePropertyBundles } from '@/hooks/payment/usePropertyBundles.ts';
import { useCreditCheckout } from '@/hooks/payment/useCreditCheckout.ts';

/**
 * Centralizes add-property entry behavior to avoid duplicating payment gating logic.
 */
export const useAddPropertyFlow = () => {
	const { resetSelectedHouse } = useHouseContext();
	const { navigateToAddNewProperty } = useAppNavigation();
	const { hasCreditsForBundle, isLoading: isCreditsLoading } = useCreditBalance();
	const { getOneOffPropertyBundle, isLoading: isBundlesLoading } = usePropertyBundles();
	const {
		initiateCheckout,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isProcessingCheckout,
	} = useCreditCheckout();

	const startAddProperty = useCallback(async () => {
		const bundle = getOneOffPropertyBundle();
		if (!bundle) {
			resetSelectedHouse();
			await navigateToAddNewProperty();
			return;
		}

		if (hasCreditsForBundle(bundle.property_slots)) {
			resetSelectedHouse();
			await navigateToAddNewProperty();
			return;
		}

		await initiateCheckout(bundle.id);
	}, [getOneOffPropertyBundle, hasCreditsForBundle, initiateCheckout, navigateToAddNewProperty, resetSelectedHouse]);

	return {
		startAddProperty,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isCheckingOut: isCreditsLoading || isBundlesLoading || isProcessingCheckout,
		canStartFlow: !isCreditsLoading && !isBundlesLoading,
	};
};
